import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import groupBy from 'lodash/groupBy';
import { FaArrowLeft, FaPlus } from 'react-icons/fa';

import { Button, Card } from '_components/_core';

import { VARIABLES } from '../utilities';
import { StyledListGroupItem, StyledListGroup, StyledVariableGroups } from './styles';

pluralize.addIrregularRule('item', 'itens');

function VariablesList({ onAddVariable, onAddCustomVariableWithType, customVariables }) {
  const [selectedGroup, setSelectedGroup] = React.useState('DEFAULT');
  const [showCustomVariableForm, setShowCustomVariableForm] = useState(false);
  const [customVariableName, setCustomVariableName] = useState('');
  const [customVariableType, setCustomVariableType] = useState('TEXTO');

  const groupedVariables = useMemo(() => {
    const validVariables = VARIABLES.filter(variable => !variable.hidden);
    return groupBy(validVariables, 'group');
  }, []);

  const groups = useMemo(() => Object.keys(groupedVariables), [groupedVariables]);

  const TRANSLATION = {
    DEFAULT: 'Sistema',
    COMPANY: 'Empresa',
    TRANSACTION: 'Transação',
    CONTACT: 'Contato',
  };

  const handleAddCustomVariable = () => {
    if (customVariableName.trim()) {
      onAddCustomVariableWithType(customVariableName.trim(), customVariableType);

      setCustomVariableName('');
      setShowCustomVariableForm(false);
    }
  };

  return (
    <>
      <StyledVariableGroups>
        {groups.map(group => (
          <StyledListGroupItem
            key={group}
            onClick={() => setSelectedGroup(group)}
            active={selectedGroup === group}
          >
            {TRANSLATION[group] || group}
            <span className="badge badge-primary badge-pill ml-2">
              {groupedVariables[group].length}
            </span>
          </StyledListGroupItem>
        ))}
      </StyledVariableGroups>
      <StyledListGroup>
        <small className="text-muted text-uppercase mt-2 mb-1">
          Variáveis de {TRANSLATION[selectedGroup] || selectedGroup}
        </small>
        {(groupedVariables[selectedGroup] || []).map(variable => (
          <StyledListGroupItem
            key={variable.name}
            onClick={() => onAddVariable(variable)}
          >
            <Button className="m-0 p-1 mr-3" variant="default">
              <FaArrowLeft />
            </Button>
            <div className="d-flex flex-column">
              <div>
                <strong role="button" tabIndex={0}>
                  {variable.name}
                </strong>
              </div>
              <small className="text-muted">{variable.description}</small>
            </div>
          </StyledListGroupItem>
        ))}
      </StyledListGroup>
      <div className="mb-3">
        <div className="mt-4 custom-variables-section">
          <div className="d-flex align-items-center justify-content-between">
            <h5>Variáveis personalizadas</h5>
            <Button
              variant="outline-primary"
              size="sm"
              className="d-flex align-items-center"
              onClick={() => setShowCustomVariableForm(!showCustomVariableForm)}
            >
              <FaPlus className="mr-2" />
              Adicionar
            </Button>
          </div>
          <p className="text-muted small mb-3">
            Estas variáveis poderão ser preenchidas manualmente ao gerar o contrato
          </p>
          {showCustomVariableForm && (
            <Card className="mt-2 p-3 border rounded mb-3">
              <div className="form-group">
                <label htmlFor="customVariableName">
                  Nome da variável (sem acentuação)
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="customVariableName"
                  value={customVariableName}
                  onChange={e => setCustomVariableName(e.target.value)}
                  placeholder="Ex: NOME_REPRESENTANTE"
                />
              </div>
              {/* <div className="form-group">
                <label htmlFor="customVariableType">Tipo de campo</label>
                <select
                  className="form-control"
                  id="customVariableType"
                  value={customVariableType}
                  onChange={e => setCustomVariableType(e.target.value)}
                >
                  {VARIABLE_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div> */}
              <div className="d-flex justify-content-end">
                <Button
                  variant="secondary"
                  size="sm"
                  className="mr-2"
                  onClick={() => setShowCustomVariableForm(false)}
                >
                  Cancelar
                </Button>
                <Button variant="primary" size="sm" onClick={handleAddCustomVariable}>
                  Adicionar
                </Button>
              </div>
            </Card>
          )}
          {customVariables.length > 0 && (
            <ul className="list-group">
              {customVariables.map((variable, index) => (
                <li
                  key={index}
                  className="list-group-item d-flex align-items-center"
                  style={{
                    fontWeight: '400',
                  }}
                >
                  <span className="badge badge-primary mr-2">{index + 1}</span>
                  {variable.name}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}

VariablesList.defaultProps = {
  onAddCustomVariableWithType: () => {},
  customVariables: [],
};

VariablesList.propTypes = {
  onAddVariable: PropTypes.func.isRequired,
  onAddCustomVariableWithType: PropTypes.func,
  customVariables: PropTypes.array,
};

export default VariablesList;
